import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { fmtDate, fmtTime } from "@/components/SessionCard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Users, ShieldAlert, UserPlus, X } from "lucide-react";

export const PANAMA_TZ = "America/Panama";
export const CREDIT_TYPES = ["fitness", "calisthenics", "youth_baby", "youth_kids", "youth_teens", "pilates_group", "pilates_private", "private"];
export const toPanamaParts = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-CA", { timeZone: PANAMA_TZ }),
    time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: PANAMA_TZ }),
  };
};
export const durationOf = (s) => Math.max(15, Math.round((new Date(s.end) - new Date(s.start)) / 60000));

const selectCls = "mt-1.5 h-11 w-full rounded-lg border border-border px-3";

export function ClassTypesSection({ classTypes, onNew, onEdit }) {
  const { tl } = useI18n();
  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold tracking-tight">{tl({ es: "Tipos de clase", en: "Class types", fr: "Types de cours" })}</h1>
        <Button data-testid="new-classtype-btn" onClick={onNew} variant="outline" className="rounded-full"><Plus className="mr-1 h-4 w-4" />{tl({ es: "Nuevo tipo", en: "New type", fr: "Nouveau type" })}</Button>
      </div>
      <div className="mb-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {classTypes.map((c) => (
          <div key={c.class_type_id} data-testid={`admin-classtype-${c.class_type_id}`} className="flex items-center justify-between rounded-2xl border border-border bg-card p-4">
            <div>
              <div className="font-medium">{tl(c.name)}</div>
              <div className="text-xs text-muted-foreground">{c.universe} · {c.duration_min}min · {c.capacity} pl · {c.credit_type}</div>
            </div>
            <Button size="sm" variant="ghost" data-testid={`edit-classtype-${c.class_type_id}`} onClick={() => onEdit(c)} className="rounded-full"><Pencil className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </>
  );
}

export function SessionsList({ sessions, lang, onEdit, onCancel }) {
  const { t, tl } = useI18n();
  return (
    <div className="space-y-2">
      {sessions.map((s) => (
        <div key={s.session_id} data-testid={`admin-session-${s.session_id}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
          <div>
            <div className="font-medium">{tl(s.class_type?.name)} <span className="text-xs text-muted-foreground">· {s.universe}</span></div>
            <div className="text-sm text-muted-foreground">{fmtDate(s.start, lang)} {fmtTime(s.start, lang)} · {s.coach?.name} · {s.booked_count}/{s.capacity}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid={`edit-session-${s.session_id}`} onClick={() => onEdit(s)} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" />{tl({ es: "Editar", en: "Edit", fr: "Modifier" })}</Button>
            <Button size="sm" variant="outline" data-testid={`cancel-session-${s.session_id}`} onClick={() => onCancel(s.session_id)} className="rounded-full text-destructive">{t("admin.cancelSession")}</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function SessionFormDialog({ open, setOpen, form, setForm, classTypes, coaches, rooms, onCreate }) {
  const { t, tl } = useI18n();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("admin.newSession")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Clase</Label>
            <select data-testid="session-classtype" value={form.class_type_id} onChange={(e) => { const c = classTypes.find(x => x.class_type_id === e.target.value); setForm({ ...form, class_type_id: e.target.value, universe: c?.universe || "fitness", duration: c?.duration_min || 60, capacity: c?.capacity || 12 }); }} className={selectCls}>
              <option value="">—</option>
              {classTypes.map(c => <option key={c.class_type_id} value={c.class_type_id}>{tl(c.name)} ({c.universe})</option>)}
            </select>
          </div>
          <div><Label>Coach</Label>
            <select data-testid="session-coach" value={form.coach_id} onChange={(e) => setForm({ ...form, coach_id: e.target.value })} className={selectCls}>
              <option value="">—</option>
              {coaches.map(c => <option key={c.coach_id} value={c.coach_id}>{c.name}</option>)}
            </select>
          </div>
          <div><Label>Sala</Label>
            <select data-testid="session-room" value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })} className={selectCls}>
              <option value="">—</option>
              {rooms.map(r => <option key={r.room_id} value={r.room_id}>{r.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Fecha</Label><Input data-testid="session-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>Hora</Label><Input data-testid="session-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5 h-11" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Duración (min)</Label><Input data-testid="session-duration" type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>Capacidad</Label><Input data-testid="session-capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>Repetir (sem)</Label><Input data-testid="session-recurring" type="number" value={form.recurring_weeks} onChange={(e) => setForm({ ...form, recurring_weeks: e.target.value })} className="mt-1.5 h-11" /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="create-session-btn" onClick={onCreate} disabled={!form.class_type_id || !form.date} className="rounded-full">{t("common.confirm")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SessionEditDialog({ open, setOpen, edit, setEdit, classTypes, coaches, onSave, onCancel, clients = [], onChanged }) {
  const { t, tl } = useI18n();
  const [parts, setParts] = useState([]);
  const [pid, setPid] = useState("");
  const [q, setQ] = useState("");
  const [consume, setConsume] = useState(true);
  const [busy, setBusy] = useState(false);
  const refetch = () => {
    if (edit?.session_id) api.get(`/coach/sessions/${edit.session_id}/participants`).then(({ data }) => setParts(data)).catch(() => setParts([]));
  };
  useEffect(() => {
    if (open && edit?.session_id) refetch(); else { setParts([]); setPid(""); setQ(""); }
    // eslint-disable-next-line
  }, [open, edit?.session_id]);

  const enroll = async () => {
    if (!pid) return;
    setBusy(true);
    try {
      await api.post(`/admin/sessions/${edit.session_id}/enroll`, { client_id: pid, consume_credit: consume });
      toast.success(tl({ es: "Inscrito", en: "Enrolled", fr: "Inscrit" }));
      setPid(""); setQ(""); refetch(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const unenroll = async (rid) => {
    setBusy(true);
    try {
      await api.post(`/admin/sessions/${edit.session_id}/unenroll`, { reservation_id: rid, refund_credit: true });
      toast.success(tl({ es: "Retirado", en: "Removed", fr: "Retiré" }));
      refetch(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const enrolledIds = new Set(parts.map((p) => p.client?.user_id));
  const filtered = (clients || []).filter((c) => !enrolledIds.has(c.user_id) &&
    (!q || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()))).slice(0, 40);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{tl({ es: "Editar / mover clase", en: "Edit / move class", fr: "Modifier / déplacer le cours" })}</DialogTitle></DialogHeader>
        {edit && (
          <div className="space-y-3">
            <div><Label>Clase</Label>
              <select data-testid="edit-classtype" value={edit.class_type_id} onChange={(e) => setEdit({ ...edit, class_type_id: e.target.value })} className={selectCls}>
                {classTypes.map(c => <option key={c.class_type_id} value={c.class_type_id}>{tl(c.name)} ({c.universe})</option>)}
              </select>
            </div>
            <div><Label>Coach</Label>
              <select data-testid="edit-coach" value={edit.coach_id} onChange={(e) => setEdit({ ...edit, coach_id: e.target.value })} className={selectCls}>
                <option value="">—</option>
                {coaches.map(c => <option key={c.coach_id} value={c.coach_id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha</Label><Input data-testid="edit-date" type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Hora</Label><Input data-testid="edit-time" type="time" value={edit.time} onChange={(e) => setEdit({ ...edit, time: e.target.value })} className="mt-1.5 h-11" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duración (min)</Label><Input data-testid="edit-duration" type="number" value={edit.duration} onChange={(e) => setEdit({ ...edit, duration: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Capacidad</Label><Input data-testid="edit-capacity" type="number" min={edit.booked} value={edit.capacity} onChange={(e) => setEdit({ ...edit, capacity: e.target.value })} className="mt-1.5 h-11" /></div>
            </div>
            {edit.booked > 0 && <p className="text-xs text-muted-foreground">{tl({ es: `${edit.booked} reservas: se notificará a los clientes si mueves la clase.`, en: `${edit.booked} bookings: clients will be notified if you move it.`, fr: `${edit.booked} réservations : les clients seront notifiés en cas de déplacement.` })}</p>}
            <div className="rounded-xl border border-border p-3" data-testid="edit-participants">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                <Users className="h-4 w-4" />{tl({ es: "Inscritos", en: "Registered", fr: "Inscrits" })}: {parts.length}/{edit.capacity}
              </div>
              {parts.length === 0 ? (
                <p className="text-sm text-muted-foreground">{tl({ es: "Nadie inscrito todavía", en: "No one registered yet", fr: "Personne d'inscrit pour l'instant" })}</p>
              ) : (
                <ul className="space-y-1">
                  {parts.map((p) => (
                    <li key={p.reservation_id} data-testid={`edit-participant-${p.reservation_id}`} className="flex items-center justify-between gap-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1.5"><span className="truncate">{p.client?.name}</span>
                        {p.client?.health_flag === "attention" && <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-destructive" />}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="text-xs capitalize text-muted-foreground">{p.status}</span>
                        <button data-testid={`unenroll-${p.reservation_id}`} disabled={busy} onClick={() => unenroll(p.reservation_id)}
                          className="rounded-full p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" title={tl({ es: "Retirar", en: "Remove", fr: "Retirer" })}>
                          <X className="h-4 w-4" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 border-t border-border pt-3">
                <Label className="text-xs">{tl({ es: "Inscribir un cliente", en: "Enroll a client", fr: "Inscrire un client" })}</Label>
                <Input data-testid="enroll-search" value={q} onChange={(e) => { setQ(e.target.value); setPid(""); }} placeholder={tl({ es: "Buscar por nombre o email", en: "Search by name or email", fr: "Rechercher par nom ou email" })} className="mt-1.5 h-10" />
                {q && (
                  <div className="mt-1 max-h-36 overflow-y-auto rounded-lg border border-border">
                    {filtered.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground">—</p> : filtered.map((c) => (
                      <button key={c.user_id} data-testid={`enroll-option-${c.user_id}`} onClick={() => { setPid(c.user_id); setQ(c.name); }}
                        className={`block w-full px-3 py-2 text-left text-sm hover:bg-accent ${pid === c.user_id ? "bg-accent" : ""}`}>
                        {c.name} <span className="text-xs text-muted-foreground">{c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <input data-testid="enroll-consume" type="checkbox" checked={consume} onChange={(e) => setConsume(e.target.checked)} className="h-4 w-4" />
                  {tl({ es: "Descontar 1 sesión del paquete", en: "Debit 1 session from pack", fr: "Débiter 1 séance du forfait" })}
                </label>
                <Button data-testid="enroll-btn" size="sm" disabled={!pid || busy} onClick={enroll} className="mt-2 rounded-full">
                  <UserPlus className="mr-1.5 h-4 w-4" />{tl({ es: "Inscribir", en: "Enroll", fr: "Inscrire" })}
                </Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="flex-row justify-between sm:justify-between">
          {onCancel && edit && (
            <Button variant="outline" data-testid="edit-cancel-session-btn" onClick={() => onCancel(edit.session_id)} className="rounded-full text-destructive">
              {t("admin.cancelSession")}
            </Button>
          )}
          <Button data-testid="save-session-btn" onClick={onSave} className="rounded-full">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ClassTypeDialog({ open, setOpen, ct, setCt, onSave }) {
  const { t, tl } = useI18n();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{ct?.class_type_id ? tl({ es: "Editar tipo de clase", en: "Edit class type", fr: "Modifier le type de cours" }) : tl({ es: "Nuevo tipo de clase", en: "New class type", fr: "Nouveau type de cours" })}</DialogTitle></DialogHeader>
        {ct && (
          <div className="space-y-3">
            <div><Label>Nombre (ES)</Label><Input data-testid="ct-name-es" value={ct.name_es} onChange={(e) => setCt({ ...ct, name_es: e.target.value })} className="mt-1.5 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name (EN)</Label><Input data-testid="ct-name-en" value={ct.name_en} onChange={(e) => setCt({ ...ct, name_en: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Nom (FR)</Label><Input data-testid="ct-name-fr" value={ct.name_fr} onChange={(e) => setCt({ ...ct, name_fr: e.target.value })} className="mt-1.5 h-11" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Universo</Label>
                <select data-testid="ct-universe" value={ct.universe} onChange={(e) => setCt({ ...ct, universe: e.target.value })} className={selectCls}>
                  <option value="fitness">Fitness</option><option value="pilates">Pilates</option>
                </select>
              </div>
              <div><Label>Tipo de crédito</Label>
                <select data-testid="ct-credit-type" value={ct.credit_type} onChange={(e) => setCt({ ...ct, credit_type: e.target.value })} className={selectCls}>
                  {CREDIT_TYPES.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duración (min)</Label><Input data-testid="ct-duration" type="number" value={ct.duration_min} onChange={(e) => setCt({ ...ct, duration_min: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Capacidad</Label><Input data-testid="ct-capacity" type="number" value={ct.capacity} onChange={(e) => setCt({ ...ct, capacity: e.target.value })} className="mt-1.5 h-11" /></div>
            </div>
            <div>
              <Label>{tl({ es: "Límite de cancelación (horas)", en: "Cancellation deadline (hours)", fr: "Délai d'annulation (heures)" })}</Label>
              <Input data-testid="ct-cancel-hours" type="number" min={0} value={ct.cancel_hours}
                onChange={(e) => setCt({ ...ct, cancel_hours: e.target.value })} className="mt-1.5 h-11"
                placeholder={tl({ es: "Por defecto del universo", en: "Universe default", fr: "Défaut de l'univers" })} />
              <p className="mt-1 text-xs text-muted-foreground">{tl({
                es: "Horas antes del inicio en que se cierra la cancelación. Vacío = usar el valor por defecto del universo (Fitness/Pilates).",
                en: "Hours before start when cancellation closes. Empty = use the universe default (Fitness/Pilates).",
                fr: "Heures avant le début où l'annulation se ferme. Vide = utiliser le défaut de l'univers (Fitness/Pilates)." })}</p>
            </div>
          </div>
        )}
        <DialogFooter><Button data-testid="save-classtype-btn" onClick={onSave} disabled={!ct?.name_es} className="rounded-full">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteWeekDialog({ open, setOpen, del, setDel, onConfirm }) {
  const { tl } = useI18n();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>{tl({ es: "Eliminar semana", en: "Delete week", fr: "Supprimer la semaine" })}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{tl({ es: "Elimina todas las clases de la semana elegida. Las demás semanas no se ven afectadas. Las reservas se reembolsan automáticamente.", en: "Deletes every class in the chosen week. Other weeks are unaffected. Bookings are automatically refunded.", fr: "Supprime tous les cours de la semaine choisie. Les autres semaines ne sont pas affectées. Les réservations sont remboursées automatiquement." })}</p>
          <div><Label>{tl({ es: "Semana a eliminar (cualquier día)", en: "Week to delete (any day)", fr: "Semaine à supprimer (n'importe quel jour)" })}</Label><Input data-testid="del-week-date" type="date" value={del.source_date} onChange={(e) => setDel({ ...del, source_date: e.target.value })} className="mt-1.5 h-11" /></div>
        </div>
        <DialogFooter><Button data-testid="del-week-confirm-btn" variant="outline" onClick={onConfirm} disabled={!del.source_date} className="rounded-full text-destructive">{tl({ es: "Eliminar", en: "Delete", fr: "Supprimer" })}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DuplicateWeekDialog({ open, setOpen, dup, setDup, onConfirm }) {
  const { t, tl } = useI18n();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>{tl({ es: "Duplicar semana", en: "Duplicate week", fr: "Dupliquer la semaine" })}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{tl({ es: "Copia todas las clases de la semana elegida hacia las próximas semanas.", en: "Copies every class from the chosen week to the next weeks.", fr: "Copie tous les cours de la semaine choisie vers les semaines suivantes." })}</p>
          <div><Label>{tl({ es: "Semana a copiar (cualquier día)", en: "Week to copy (any day)", fr: "Semaine à copier (n'importe quel jour)" })}</Label><Input data-testid="dup-date" type="date" value={dup.source_date} onChange={(e) => setDup({ ...dup, source_date: e.target.value })} className="mt-1.5 h-11" /></div>
          <div><Label>{tl({ es: "Nº de semanas a generar", en: "Number of weeks to generate", fr: "Nombre de semaines à générer" })}</Label><Input data-testid="dup-weeks" type="number" min={1} max={12} value={dup.weeks} onChange={(e) => setDup({ ...dup, weeks: e.target.value })} className="mt-1.5 h-11" /></div>
        </div>
        <DialogFooter><Button data-testid="dup-confirm-btn" onClick={onConfirm} disabled={!dup.source_date} className="rounded-full">{t("common.confirm")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
